import { ref, onScopeDispose, getCurrentScope } from 'vue';

/**
 * Shared reactive clock.
 *
 * Relative-time output (`$filters.timeago`, "overdue" styling, countdowns) is
 * computed during render from `Date.now()`, so it freezes the moment the
 * component stops re-rendering: a task due "in 24 hours" still reads "in 24
 * hours" many hours later. Reading `now.value` while rendering registers a
 * reactive dependency on this clock, so every tick re-renders those labels.
 *
 * One module-level timer serves the whole app (not one per component), and it
 * only runs while at least one scope is subscribed. While the tab is hidden the
 * interval is cleared — browsers throttle background timers anyway — and
 * becoming visible ticks immediately, so labels are correct the instant the
 * user comes back, before any network round-trip.
 */
const TICK_INTERVAL = 30000;

const now = ref(Date.now());

let timer = null;
let subscribers = 0;

function tick() {
    now.value = Date.now();
}

function startTimer() {
    if (timer || document.visibilityState === 'hidden') {
        return;
    }
    timer = setInterval(tick, TICK_INTERVAL);
}

function stopTimer() {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

function onVisibilityChange() {
    if (!subscribers) {
        return;
    }
    if (document.visibilityState === 'visible') {
        tick();
        startTimer();
    } else {
        stopTimer();
    }
}

function subscribe() {
    subscribers += 1;
    if (subscribers === 1) {
        document.addEventListener('visibilitychange', onVisibilityChange);
        tick();
        startTimer();
    }
}

function unsubscribe() {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        stopTimer();
    }
}

/**
 * @returns {{ now: import('vue').Ref<number> }} millisecond timestamp, ticking every 30s
 */
export function useNow() {
    subscribe();

    if (getCurrentScope()) {
        onScopeDispose(unsubscribe);
    }

    return { now };
}
